'use client';

import ProductCard, { type Product } from '@/components/Product/ProductCard';
import { useJump } from '@/components/MagicJump';
import { AppStoreContext } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EProductImageType } from '@/utils/enums/goods';
import { useTranslations } from 'next-intl';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ContextType,
  type MouseEvent,
  type ReactNode,
} from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { resolveBestsellerDisplayCurrency } from './bestsellerPrice';
import { toProduct } from './productAdapter';
import type { BestsellerProduct, BestsellerVibe } from './types';
import {
  getVibePageProducts,
  mergeVibeProducts,
  resolveAvailableVibes,
  shouldLoadMoreVibeProducts,
  VIBE_AUTOPLAY_MS,
  VIBE_HOVER_IMAGE_RULE,
  VIBE_PAGE_SIZE,
  VIBE_TRANSITION_MS,
} from './vibeCarousel';
import { loadVibeProductPage } from './vibeProductLoader';
import { registerVibeReturnScrollRestore } from './vibeReturnScroll';
import {
  BESTSELLER_VIBE_ARROW_CLASS_NAME,
  BESTSELLER_VIBE_ARROW_ICON_CLASS_NAME,
  BESTSELLER_VIBE_ARROW_SIDE_CLASS_NAMES,
} from './bestsellerStyles';
import {
  BESTSELLER_TRACKING_MODULES,
  createBestsellerProductTrackingHandlers,
  type BestsellerProductTracking,
} from './bestsellerTracking';
import styles from './VibeSection.module.css';

interface VibeLoadState {
  products: BestsellerProduct[];
  total: number;
  nextPage: number;
  hasMore: boolean;
  loading: boolean;
  failed: boolean;
}

type VibeAutoplayOverride = 'default' | 'paused' | 'playing';

type AppStoreApi = NonNullable<ContextType<typeof AppStoreContext>>;

function VibeArrowIcon({
  direction,
  className,
}: {
  direction: 'previous' | 'next';
  className?: string;
}) {
  return (
    <svg
      aria-hidden
      className={`${className ?? ''} ${direction === 'previous' ? 'rotate-180' : ''}`}
      data-vibe-arrow-icon={direction}
      fill="none"
      viewBox="0 0 16 16"
    >
      <path
        d="M6 3.5L10.5 8L6 12.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.2"
      />
    </svg>
  );
}

function VibeDisplayCurrencyScope({ children }: { children: ReactNode }) {
  const parentStore = useContext(AppStoreContext);
  const scopedStore = useMemo<AppStoreApi | null>(() => {
    if (!parentStore) return null;
    const scopedStateCache = new WeakMap<
      ReturnType<AppStoreApi['getState']>,
      ReturnType<AppStoreApi['getState']>
    >();

    const withDisplayCurrency = (state: ReturnType<AppStoreApi['getState']>) => {
      const displayCurrency = resolveBestsellerDisplayCurrency(state.currency);
      if (displayCurrency === state.currency) return state;
      const cachedState = scopedStateCache.get(state);
      if (cachedState) return cachedState;

      const scopedState = { ...state, currency: displayCurrency };
      scopedStateCache.set(state, scopedState);
      return scopedState;
    };

    return {
      ...parentStore,
      getState: () => withDisplayCurrency(parentStore.getState()),
      getInitialState: () => withDisplayCurrency(parentStore.getInitialState()),
      subscribe: (listener) =>
        parentStore.subscribe((state, previousState) =>
          listener(withDisplayCurrency(state), withDisplayCurrency(previousState)),
        ),
    };
  }, [parentStore]);

  if (!scopedStore) return children;
  return <AppStoreContext.Provider value={scopedStore}>{children}</AppStoreContext.Provider>;
}

function VibeProductCard({
  product,
  index,
  tabName,
  tracking,
  wishStates,
  onWishChange,
}: {
  product: BestsellerProduct;
  index: number;
  tabName: string;
  tracking: BestsellerProductTracking;
  wishStates: Record<string, number>;
  onWishChange: (shopSku: string, isWish: number) => void;
}) {
  const toJumpTarget = useJump();
  const commonProduct = useMemo(() => {
    const normalizedProduct = toProduct(product);
    const currentWishState = wishStates[normalizedProduct.shop_sku];
    const correlation = normalizedProduct.correlation?.map((correlatedProduct) => {
      const correlatedWishState = wishStates[correlatedProduct.shop_sku];
      return correlatedWishState === undefined
        ? correlatedProduct
        : { ...correlatedProduct, is_wish: correlatedWishState };
    });

    if (currentWishState === undefined && correlation === normalizedProduct.correlation) {
      return normalizedProduct;
    }

    return {
      ...normalizedProduct,
      ...(currentWishState === undefined ? {} : { is_wish: currentWishState }),
      correlation,
    } as Product;
  }, [product, wishStates]);
  const trackingHandlers = createBestsellerProductTrackingHandlers({
    tracking,
    product,
    index,
    moduleName: BESTSELLER_TRACKING_MODULES.vibe,
    tabName,
  });
  const currentProductRef = useRef<Product>(commonProduct);

  useEffect(() => {
    currentProductRef.current = commonProduct;
  }, [commonProduct]);

  const rememberCurrentProduct = (_productIndex?: number, currentProduct = commonProduct) => {
    currentProductRef.current = currentProduct;
    return currentProduct;
  };

  const handleCardClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const correlationOption = target.closest<HTMLElement>('[role="radio"]');

    if (correlationOption) {
      const options = Array.from(event.currentTarget.querySelectorAll('[role="radio"]'));
      const correlationIndex = options.indexOf(correlationOption);
      const correlatedProduct = commonProduct.correlation?.[correlationIndex];
      if (correlatedProduct) {
        currentProductRef.current = {
          ...commonProduct,
          ...correlatedProduct,
          correlation: commonProduct.correlation,
        } as Product;
      }
      return;
    }

    if (!target.closest('button')) return;
    const currentProduct = currentProductRef.current;
    trackingHandlers.onCollect(currentProduct.is_wish === 1 ? '取消收藏' : '收藏', currentProduct);
  };

  const openCurrentProductFromKeyboard = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const currentProduct = currentProductRef.current;
    registerVibeReturnScrollRestore({
      pageUrl: `${window.location.pathname}${window.location.search}`,
    });
    trackingHandlers.onProductClick(index, currentProduct);
    toJumpTarget({
      jump_type: 4,
      Jump_target: `/goods-detail/${currentProduct.shop_sku}`,
    });
  };

  return (
    <div
      className="relative min-w-0"
      data-bestseller-collect-tracker
      onClickCapture={handleCardClickCapture}
    >
      <a
        href={`/goods-detail/${commonProduct.shop_sku}`}
        aria-label={product.name}
        className="pointer-events-none absolute inset-0 z-[3] rounded-[8px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#463ae8]"
        data-vibe-product-link
        onClick={openCurrentProductFromKeyboard}
      />
      <ProductCard
        product={commonProduct}
        className={`${styles.vibeProductCard} h-[265px] min-w-0 md:h-auto [&_.ProductImageContainer>div.absolute]:!p-[8px] [&_.ProductInfo]:!h-[48px] [&_.ProductInfo]:!min-h-[48px] [&_.ProductInfo]:!px-[3px] [&_.ProductInfo]:!pb-0 md:[&_.ProductImageContainer>div.absolute]:!p-[12px] md:[&_.ProductInfo]:!h-auto md:[&_.ProductInfo]:!min-h-[69px] md:[&_.ProductInfo]:!px-[8px] md:[&_.ProductInfo]:!pt-[6px] md:[&_.ProductInfo>div:first-child]:!h-auto md:[&_.ProductInfo>div:first-child>div.flex]:!h-auto md:[&_.ProductInfo>div:first-child>div.flex]:!min-h-0 md:[&_.ProductInfo>div:first-child>div.flex]:!flex-wrap md:[&_.ProductInfo>div:first-child>div.flex]:!pt-0 md:[&_.ProductInfo>div:nth-child(2)]:!mt-[4px] md:[&_.ProductInfo>div:nth-child(3)]:!mt-[6px] md:[&_.ProductInfo>div:nth-child(3)]:!h-[11px] md:[&_.ProductInfo>div:nth-child(3)]:!pb-0 xl:[&_.ProductImageContainer>div.absolute]:!p-[14px] xl:[&_.ProductInfo]:!h-auto xl:[&_.ProductInfo]:!min-h-[77px] xl:[&_.ProductInfo]:!px-[10px] xl:[&_.ProductInfo]:!pt-[9px] xl:[&_.ProductInfo>div:first-child]:!h-auto xl:[&_.ProductInfo>div:first-child>div.flex]:!h-auto xl:[&_.ProductInfo>div:first-child>div.flex]:!flex-wrap xl:[&_.ProductInfo>div:nth-child(2)]:!mt-[5px] xl:[&_.ProductInfo>div:nth-child(3)]:!mt-[8px] xl:[&_.ProductInfo>div:nth-child(3)]:!h-[14px] 2xl:[&_.ProductImageContainer>div.absolute]:!p-[20px] 2xl:[&_.ProductInfo]:!h-auto 2xl:[&_.ProductInfo]:!min-h-[92px] 2xl:[&_.ProductInfo]:!px-[16px] 2xl:[&_.ProductInfo]:!pt-[13px] 2xl:[&_.ProductInfo>div:first-child]:!h-auto 2xl:[&_.ProductInfo>div:first-child>div.flex]:!h-auto 2xl:[&_.ProductInfo>div:first-child>div.flex]:!flex-wrap 2xl:[&_.ProductInfo>div:nth-child(2)]:!mt-[8px] 2xl:[&_.ProductInfo>div:nth-child(3)]:!mt-[11px] 2xl:[&_.ProductInfo>div:nth-child(3)]:!h-[21px]`}
        imageClassName="!aspect-square !rounded-[8px] md:!aspect-[11/8] md:!rounded-12"
        imageRule={[EProductImageType.PRODUCT]}
        imageHoverRule={VIBE_HOVER_IMAGE_RULE}
        showCorrelation
        showTryOn={false}
        showTag={false}
        showMember={false}
        showName
        namePriceInline
        frameWidth="80%"
        collectScene="bestsellers氛围商品"
        index={index}
        handelClick={(productIndex, currentProduct) => {
          registerVibeReturnScrollRestore({
            pageUrl: `${window.location.pathname}${window.location.search}`,
          });
          trackingHandlers.onProductClick(
            productIndex,
            rememberCurrentProduct(productIndex, currentProduct),
          );
        }}
        onValidExposure={(productIndex, currentProduct) => {
          trackingHandlers.onProductExposure(
            productIndex,
            rememberCurrentProduct(productIndex, currentProduct),
          );
        }}
        wished={() => {
          currentProductRef.current = { ...currentProductRef.current, is_wish: 1 };
          onWishChange(currentProductRef.current.shop_sku, 1);
        }}
        removed={() => {
          currentProductRef.current = { ...currentProductRef.current, is_wish: 2 };
          onWishChange(currentProductRef.current.shop_sku, 2);
        }}
      />
    </div>
  );
}

function VibeTabs({
  items,
  activeId,
  onChange,
}: {
  items: BestsellerVibe[];
  activeId: string;
  onChange: (id: string) => void;
}) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeIndex = items.findIndex((item) => item.id === activeId);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const activeItem = itemRefs.current[activeIndex];
    if (!container || !activeItem || activeIndex < 0) return;

    const containerRect = container.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();
    const isFullyVisible =
      itemRect.left >= containerRect.left && itemRect.right <= containerRect.right;
    if (isFullyVisible) return;

    const itemLeft = container.scrollLeft + itemRect.left - containerRect.left;
    const scrollLeft = itemLeft - (container.clientWidth - itemRect.width) / 2;
    container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
  }, [activeId, activeIndex]);

  return (
    <div
      ref={scrollContainerRef}
      data-vibe-tabs
      className={`flex max-w-full gap-[8px] overflow-x-auto px-[8px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:justify-center md:overflow-visible md:px-0 xl:gap-[10px] 2xl:gap-[16px] ${
        items.length <= 4 ? styles.compactVibeTabs : ''
      }`}
    >
      {items.map((item, index) => {
        const active = item.id === activeId;
        return (
          <button
            ref={(node) => {
              itemRefs.current[index] = node;
            }}
            key={item.id}
            type="button"
            onClick={() => onChange(item.id)}
            className={`h-[28px] shrink-0 rounded-[32px] border-0 px-[12px] font-br text-[12px] leading-[14px] transition-colors md:h-[22px] md:cursor-pointer xl:h-[28px] xl:px-[22px] 2xl:h-[44px] 2xl:px-[32px] 2xl:text-[16px] 2xl:leading-[19px] ${
              active
                ? 'min-w-[42px] bg-[#282828] text-white md:min-w-[48px] xl:min-w-[58px] 2xl:min-w-[83px]'
                : 'bg-[#eeeeee] text-[#666666] hover:text-[#282828]'
            }`}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

function resolveVisibleVibes(
  vibes: BestsellerVibe[],
  availableVibes: BestsellerVibe[],
  loadableEmptyVibes: BestsellerVibe[],
): BestsellerVibe[] {
  const visibleVibeById = new Map(
    [...availableVibes, ...loadableEmptyVibes].map((vibe) => [vibe.id, vibe]),
  );
  return vibes.flatMap((vibe) => {
    const visibleVibe = visibleVibeById.get(vibe.id);
    return visibleVibe ? [visibleVibe] : [];
  });
}

export default function VibeSection({
  title,
  subtitle,
  vibes,
  products,
  tracking,
}: {
  title: string;
  subtitle: string;
  vibes: BestsellerVibe[];
  products: BestsellerProduct[];
  tracking: BestsellerProductTracking;
}) {
  const t = useTranslations('activity.campaigns.bestsellers');
  const [vibeLoadStates, setVibeLoadStates] = useState<Record<string, VibeLoadState>>(() =>
    Object.fromEntries(
      vibes.map((vibe, index) => {
        const initialProducts = vibe.products ?? (index === 0 ? products : []);
        const total = Math.max(vibe.total ?? initialProducts.length, initialProducts.length);
        const hasMore = vibe.hasMore ?? total > initialProducts.length;
        const canLoadProducts = Boolean(vibe.productGroupId);
        const needsInitialPage = canLoadProducts && initialProducts.length === 0 && hasMore;
        return [
          vibe.id,
          {
            products: initialProducts,
            total,
            nextPage: needsInitialPage ? 1 : 2,
            hasMore: Boolean(canLoadProducts && (needsInitialPage || hasMore)),
            loading: false,
            failed: false,
          },
        ];
      }),
    ),
  );
  const loadingVibeIds = useRef(new Set<string>());
  const vibeSwiperRef = useRef<SwiperType | null>(null);
  const vibeCarouselRef = useRef<HTMLDivElement | null>(null);
  const loadedVibes = useMemo(
    () =>
      vibes.map((vibe) => {
        const loadState = vibeLoadStates[vibe.id];
        return {
          ...vibe,
          products: loadState?.products ?? vibe.products,
          total: loadState?.total ?? vibe.total,
        };
      }),
    [vibeLoadStates, vibes],
  );
  const availableVibes = useMemo(
    () => resolveAvailableVibes(loadedVibes, products),
    [loadedVibes, products],
  );
  const loadableEmptyVibes = useMemo(
    () =>
      loadedVibes.filter((vibe) => {
        const loadState = vibeLoadStates[vibe.id];
        const initialProducts = loadState?.products ?? vibe.products ?? [];
        return initialProducts.length === 0 && Boolean(vibe.productGroupId && loadState?.hasMore);
      }),
    [loadedVibes, vibeLoadStates],
  );
  const visibleVibes = useMemo(
    () => resolveVisibleVibes(loadedVibes, availableVibes, loadableEmptyVibes),
    [availableVibes, loadableEmptyVibes, loadedVibes],
  );
  const [selectedVibe, setSelectedVibe] = useState(vibes[0]?.id || '');
  const [vibePages, setVibePages] = useState<Record<string, number>>({});
  const [vibeWishStates, setVibeWishStates] = useState<Record<string, number>>({});
  const [isInteracting, setIsInteracting] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [autoplayOverride, setAutoplayOverride] = useState<VibeAutoplayOverride>('default');
  const activeVibe = visibleVibes.find((item) => item.id === selectedVibe) || visibleVibes[0];
  const activeLoadState = activeVibe ? vibeLoadStates[activeVibe.id] : undefined;
  const activeVibeProducts = activeVibe?.products || [];
  const loadedVibePageCount = Math.ceil(activeVibeProducts.length / VIBE_PAGE_SIZE);
  const shouldLoopVibePages = loadedVibePageCount > 1 && !activeLoadState?.hasMore;
  const totalVibePageCount = Math.max(
    Math.ceil((activeLoadState?.total ?? activeVibeProducts.length) / VIBE_PAGE_SIZE),
    loadedVibePageCount,
  );
  const maxLoadedVibePage = Math.max(loadedVibePageCount - 1, 0);
  const currentVibePage = activeVibe
    ? Math.min(vibePages[activeVibe.id] ?? 0, maxLoadedVibePage)
    : 0;
  const isAutoplayPaused =
    autoplayOverride === 'paused' || (autoplayOverride === 'default' && prefersReducedMotion);

  const loadMoreVibeProducts = useCallback(
    async (vibe: BestsellerVibe) => {
      const loadState = vibeLoadStates[vibe.id];
      if (
        !loadState?.hasMore ||
        loadState.loading ||
        loadState.failed ||
        !vibe.productGroupId ||
        loadingVibeIds.current.has(vibe.id)
      ) {
        return;
      }

      loadingVibeIds.current.add(vibe.id);
      setVibeLoadStates((current) => ({
        ...current,
        [vibe.id]: { ...current[vibe.id], loading: true, failed: false },
      }));

      try {
        const page = await loadVibeProductPage(vibe, loadState.nextPage);
        setVibeLoadStates((current) => {
          const currentState = current[vibe.id];
          const mergedProducts = mergeVibeProducts(currentState.products, page.products);
          return {
            ...current,
            [vibe.id]: {
              ...currentState,
              products: mergedProducts,
              total: Math.max(page.total ?? currentState.total, mergedProducts.length),
              nextPage: currentState.nextPage + 1,
              hasMore: page.hasMore,
              loading: false,
              failed: false,
            },
          };
        });
      } catch (error) {
        logger.error('Failed to lazy-load bestseller vibe products:', error);
        setVibeLoadStates((current) => ({
          ...current,
          [vibe.id]: {
            ...current[vibe.id],
            hasMore: current[vibe.id]?.hasMore ?? true,
            loading: false,
            failed: true,
          },
        }));
      } finally {
        loadingVibeIds.current.delete(vibe.id);
      }
    },
    [vibeLoadStates],
  );

  useEffect(() => {
    if (!activeVibe) return;

    setVibePages((current) => {
      const currentPage = current[activeVibe.id] ?? 0;
      if (currentPage <= maxLoadedVibePage) return current;
      return { ...current, [activeVibe.id]: maxLoadedVibePage };
    });
  }, [activeVibe, maxLoadedVibePage]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateReducedMotion = () => setPrefersReducedMotion(mediaQuery.matches);
    updateReducedMotion();
    mediaQuery.addEventListener('change', updateReducedMotion);

    return () => mediaQuery.removeEventListener('change', updateReducedMotion);
  }, []);

  useEffect(() => {
    if (isAutoplayPaused || isInteracting || activeLoadState?.loading || loadedVibePageCount <= 1) {
      return;
    }

    const timer = window.setInterval(() => {
      vibeSwiperRef.current?.slideNext();
    }, VIBE_AUTOPLAY_MS);

    return () => window.clearInterval(timer);
  }, [
    activeLoadState?.loading,
    activeVibe?.id,
    isAutoplayPaused,
    isInteracting,
    loadedVibePageCount,
  ]);

  useEffect(() => {
    const carousel = vibeCarouselRef.current;
    if (!carousel) return;

    const pauseAutoplay = () => setIsInteracting(true);
    const resumeAutoplay = () => setIsInteracting(false);

    carousel.addEventListener('mouseenter', pauseAutoplay);
    carousel.addEventListener('mouseleave', resumeAutoplay);

    return () => {
      carousel.removeEventListener('mouseenter', pauseAutoplay);
      carousel.removeEventListener('mouseleave', resumeAutoplay);
    };
  }, []);

  useEffect(() => {
    if (
      !activeVibe ||
      !activeLoadState ||
      !shouldLoadMoreVibeProducts(
        currentVibePage,
        loadedVibePageCount,
        activeLoadState.hasMore,
        activeLoadState.loading,
      )
    ) {
      return;
    }

    void loadMoreVibeProducts(activeVibe);
  }, [activeLoadState, activeVibe, currentVibePage, loadMoreVibeProducts, loadedVibePageCount]);

  const handleVibeChange = (id: string) => {
    setSelectedVibe(id);
    setVibeLoadStates((current) => {
      const loadState = current[id];
      if (!loadState?.failed) return current;
      return { ...current, [id]: { ...loadState, failed: false } };
    });
  };

  const handleVibePrev = () => {
    vibeSwiperRef.current?.slidePrev();
  };

  const handleVibeNext = () => {
    if (activeLoadState?.loading && currentVibePage >= maxLoadedVibePage) return;
    if (
      activeVibe &&
      activeLoadState &&
      shouldLoadMoreVibeProducts(
        currentVibePage,
        loadedVibePageCount,
        activeLoadState.hasMore,
        activeLoadState.loading,
      )
    ) {
      void loadMoreVibeProducts(activeVibe);
      return;
    }
    vibeSwiperRef.current?.slideNext();
  };

  const handleVibeSlideChange = (swiper: SwiperType) => {
    const vibeId = activeVibe?.id;
    if (!vibeId) return;

    setVibePages((current) => {
      if (current[vibeId] === swiper.realIndex) return current;
      return { ...current, [vibeId]: swiper.realIndex };
    });
  };

  const toggleAutoplay = () => {
    setAutoplayOverride(isAutoplayPaused ? 'playing' : 'paused');
  };

  const handleVibeWishChange = useCallback((shopSku: string, isWish: number) => {
    setVibeWishStates((current) =>
      current[shopSku] === isWish ? current : { ...current, [shopSku]: isWish },
    );
  }, []);

  if (!activeVibe) return null;

  return (
    <section
      className="overflow-hidden bg-white px-[4px] pb-[40px] pt-[40px] md:px-[11px] md:pb-[30px] md:pt-[28px] xl:px-[13px] xl:pb-[49px] xl:pt-[53px] 2xl:px-[20px] 2xl:pb-[80px] 2xl:pt-[80px]"
      data-bestseller-vibe-section
    >
      <div className="mx-auto max-w-[1880px] px-[8px] text-center md:px-0">
        <h2
          id="bestseller-vibe-heading"
          className="font-bb text-[22px] leading-[26px] font-bold text-[#282828] md:text-[20px] md:leading-[24px] xl:text-[24px] xl:leading-[29px] 2xl:text-[32px] 2xl:leading-[38px]"
        >
          {title}
        </h2>
        <p className="mx-auto mt-[8px] max-w-[351px] font-br text-[12px] leading-[14px] text-[#6c6c6c] md:max-w-[688px] xl:mt-[4px] 2xl:mt-[8px] 2xl:text-[14px] 2xl:leading-[17px]">
          {subtitle}
        </p>
      </div>
      <div className="mt-[20px] md:mt-[16px] xl:mt-[21px] 2xl:mt-[32px]">
        <VibeTabs items={visibleVibes} activeId={activeVibe.id} onChange={handleVibeChange} />
      </div>
      <div
        ref={vibeCarouselRef}
        className="relative mx-auto mt-[20px] max-w-[1880px] xl:mt-[21px] 2xl:mt-[32px]"
        data-vibe-carousel
        onPointerEnter={() => setIsInteracting(true)}
        onPointerLeave={() => setIsInteracting(false)}
        onPointerDown={() => setIsInteracting(true)}
        onPointerUp={() => setIsInteracting(false)}
        onPointerCancel={() => setIsInteracting(false)}
        onFocusCapture={() => setIsInteracting(true)}
        onBlurCapture={() => setIsInteracting(false)}
      >
        {loadedVibePageCount > 1 && (
          <button
            type="button"
            aria-label={t('previousProducts')}
            className={`${BESTSELLER_VIBE_ARROW_CLASS_NAME} ${BESTSELLER_VIBE_ARROW_SIDE_CLASS_NAMES.previous}`}
            onClick={handleVibePrev}
          >
            <VibeArrowIcon direction="previous" className={BESTSELLER_VIBE_ARROW_ICON_CLASS_NAME} />
          </button>
        )}
        <VibeDisplayCurrencyScope>
          <Swiper
            key={activeVibe.id}
            className="w-full"
            initialSlide={currentVibePage}
            loop={shouldLoopVibePages}
            speed={VIBE_TRANSITION_MS}
            allowTouchMove={loadedVibePageCount > 1}
            onSwiper={(swiper) => {
              vibeSwiperRef.current = swiper;
            }}
            onSlideChange={handleVibeSlideChange}
          >
            {Array.from({ length: loadedVibePageCount }, (_, page) => (
              <SwiperSlide
                key={`${activeVibe.id}-${page}`}
                className="!h-auto"
                data-vibe-page={page}
                inert={page !== currentVibePage}
                aria-hidden={page !== currentVibePage}
              >
                <div className="grid grid-cols-2 gap-x-[4px] gap-y-[16px] md:grid-cols-4 md:gap-[8px] xl:gap-[11px] 2xl:gap-[16px]">
                  {getVibePageProducts(activeVibeProducts, page).map((product, pageIndex) => (
                    <VibeProductCard
                      key={`${activeVibe.id}-${product.id}`}
                      product={product}
                      index={page * VIBE_PAGE_SIZE + pageIndex}
                      tabName={activeVibe.label}
                      tracking={tracking}
                      wishStates={vibeWishStates}
                      onWishChange={handleVibeWishChange}
                    />
                  ))}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </VibeDisplayCurrencyScope>
        {loadedVibePageCount > 1 && (
          <button
            type="button"
            aria-label={t('nextProducts')}
            className={`${BESTSELLER_VIBE_ARROW_CLASS_NAME} ${BESTSELLER_VIBE_ARROW_SIDE_CLASS_NAMES.next}`}
            onClick={handleVibeNext}
          >
            <VibeArrowIcon direction="next" className={BESTSELLER_VIBE_ARROW_ICON_CLASS_NAME} />
          </button>
        )}
      </div>
      <div className="relative mx-auto max-w-[1880px]">
        <div
          role="progressbar"
          aria-labelledby="bestseller-vibe-heading"
          aria-valuemin={1}
          aria-valuemax={Math.max(totalVibePageCount, 1)}
          aria-valuenow={currentVibePage + 1}
          className="relative mx-auto mt-[24px] h-[2px] w-[120px] rounded-[2px] bg-[#eeeeee] md:hidden"
        >
          <span
            className="absolute inset-y-0 left-0 rounded-[4px] bg-[#808080] transition-transform duration-300"
            style={{
              width: `${100 / Math.max(totalVibePageCount, 1)}%`,
              transform: `translateX(${currentVibePage * 100}%)`,
            }}
          />
        </div>
        {loadedVibePageCount > 1 ? (
          <button
            type="button"
            aria-label={isAutoplayPaused ? t('playCarousel') : t('pauseCarousel')}
            title={isAutoplayPaused ? t('playCarousel') : t('pauseCarousel')}
            className="sr-only flex items-center justify-center border-0 bg-white p-0 text-[#282828] focus-visible:not-sr-only focus-visible:absolute focus-visible:right-[8px] focus-visible:top-[8px] focus-visible:z-[4] focus-visible:h-[32px] focus-visible:w-[32px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#463ae8] md:focus-visible:right-0"
            data-vibe-autoplay-toggle
            onClick={toggleAutoplay}
          >
            {isAutoplayPaused ? (
              <span
                aria-hidden
                className="ml-[2px] h-0 w-0 border-y-[5px] border-y-transparent border-l-[8px] border-l-current"
              />
            ) : (
              <span aria-hidden className="flex h-[11px] items-stretch gap-[3px]">
                <span className="w-[2px] bg-current" />
                <span className="w-[2px] bg-current" />
              </span>
            )}
          </button>
        ) : null}
      </div>
    </section>
  );
}
